import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

export default function Work({ profile }) {
  const [projects, setProjects] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [showDetail, setShowDetail] = useState(null)
  const [search, setSearch] = useState('')
  const [yearFilter, setYearFilter] = useState('all')
  const [form, setForm] = useState({ year:'', month:'1월', client:'', model:'', worker:'', due:'', content:'' })
  const [images, setImages] = useState([])
  const [previews, setPreviews] = useState([])
  const [editingId, setEditingId] = useState(null)
  const [existingImageUrls, setExistingImageUrls] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => { fetchProjects() }, [])

  async function fetchProjects() {
    const { data } = await supabase.from('projects').select('*').order('created_at', { ascending: false })
    if (data) setProjects(data)
  }

  async function deleteProject(id) {
    if (!window.confirm('이 프로젝트를 삭제할까요?')) return
    await supabase.from('projects').delete().eq('id', id)
    setShowDetail(null)
    fetchProjects()
  }

  async function handleFiles(e) {
    const files = [...e.target.files]
    if (!files.length) return
    const urls = await Promise.all(files.map(f => new Promise(res => {
      const r = new FileReader(); r.onload = ev => res(ev.target.result); r.readAsDataURL(f)
    })))
    if (editingId) {
      setImages(prev => [...prev, ...files])
      setPreviews(prev => [...prev, ...urls])
    } else {
      setImages(files)
      setPreviews(urls)
    }
    e.target.value = ''
  }

  function resetModal() {
    setShowModal(false)
    setEditingId(null)
    setExistingImageUrls([])
    setForm({ year:'', month:'1월', client:'', model:'', worker:'', due:'', content:'' })
    setImages([])
    setPreviews([])
  }

  function removeNewImageAt(index) {
    setImages(prev => prev.filter((_, i) => i !== index))
    setPreviews(prev => prev.filter((_, i) => i !== index))
  }

  function openNewModal() {
    setEditingId(null)
    setExistingImageUrls([])
    setForm({ year:'', month:'1월', client:'', model:'', worker:'', due:'', content:'' })
    setImages([])
    setPreviews([])
    setShowModal(true)
  }

  function openEditModal(p) {
    setEditingId(p.id)
    setExistingImageUrls(Array.isArray(p.image_urls) ? [...p.image_urls] : [])
    setForm({
      year: p.year || '',
      month: p.month || '1월',
      client: p.client || '',
      model: p.model || '',
      worker: p.worker || '',
      due: p.due_date || '',
      content: p.content && p.content !== '(내용 없음)' ? p.content : ''
    })
    setImages([])
    setPreviews([])
    setShowDetail(null)
    setShowModal(true)
  }

  async function submitProject() {
    if (!form.year || !form.client) { alert('연도와 고객사를 입력해주세요'); return }
    setLoading(true)

    const newUploadedUrls = []
    for (const file of images) {
      const fileName = `${Date.now()}_${file.name.replace(/[^\w.-]/g, '_')}`
      const { data } = await supabase.storage.from('drawings').upload(fileName, file)
      if (data) {
        const { data: urlData } = supabase.storage.from('drawings').getPublicUrl(fileName)
        newUploadedUrls.push(urlData.publicUrl)
      }
    }

    const imageUrls = editingId ? [...existingImageUrls, ...newUploadedUrls] : newUploadedUrls

    if (editingId) {
      const { error } = await supabase.from('projects').update({
        year: form.year,
        month: form.month,
        client: form.client,
        model: form.model,
        worker: form.worker,
        due_date: form.due || null,
        content: form.content || '(내용 없음)',
        image_urls: imageUrls
      }).eq('id', editingId)
      if (error) {
        alert(error.message || '수정 실패')
        setLoading(false)
        return
      }
    } else {
      await supabase.from('projects').insert({
        year: form.year,
        month: form.month,
        client: form.client,
        model: form.model,
        worker: form.worker,
        due_date: form.due || null,
        content: form.content || '(내용 없음)',
        image_urls: imageUrls
      })
    }

    resetModal()
    fetchProjects()
    setLoading(false)
  }

  const filtered = projects.filter(p => {
    const q = search.toLowerCase()
    const match = !q || p.client?.toLowerCase().includes(q) || p.model?.toLowerCase().includes(q) || p.worker?.includes(q)
    const yMatch = yearFilter === 'all' || p.year === yearFilter
    return match && yMatch
  })

  return (
    <div>
      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>특장차 제작 이력</div>
          <div style={{ fontSize:13, color:'#a09e99' }}>연도·월·고객사 단위 · 도면 이미지 첨부</div>
        </div>
        <button type="button" onClick={openNewModal}
          style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
          + 프로젝트 등록
        </button>
      </div>

      {/* 검색/필터 */}
      <div style={{ display:'flex', gap:10, marginBottom:20 }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="고객사, 차종, 담당자 검색..."
          style={{ flex:1, padding:'9px 14px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', outline:'none' }} />
        <select value={yearFilter} onChange={e => setYearFilter(e.target.value)}
          style={{ padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', cursor:'pointer' }}>
          <option value="all">전체 연도</option>
          <option value="26">2026년</option>
          <option value="25">2025년</option>
          <option value="24">2024년</option>
        </select>
      </div>

      {/* 카드 그리드 */}
      {filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:60, color:'#a09e99' }}>
          <div style={{ fontSize:36, marginBottom:10 }}>🚛</div>
          <div style={{ fontSize:13 }}>등록된 프로젝트가 없습니다</div>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px,1fr))', gap:14 }}>
          {filtered.map(p => (
            <div key={p.id} onClick={() => setShowDetail(p)}
              style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, overflow:'hidden', cursor:'pointer', transition:'all 0.15s', boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}
              onMouseOver={e => e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.08)'}
              onMouseOut={e => e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.06)'}>
              {/* 썸네일 */}
              <div style={{ width:'100%', height:120, background:'#f9f8f6', display:'flex', alignItems:'center', justifyContent:'center', borderBottom:'1px solid #e5e3de', overflow:'hidden' }}>
                {p.image_urls?.length > 0
                  ? <img src={p.image_urls[0]} alt="도면" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  : <span style={{ fontSize:38 }}>🚛</span>}
              </div>
              <div style={{ padding:14 }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#4a8c60', letterSpacing:'0.06em', marginBottom:4 }}>
                  {p.year}년 {p.month} · {p.worker}
                </div>
                <div style={{ fontSize:15, fontWeight:700, marginBottom:3 }}>{p.client}</div>
                <div style={{ fontSize:11, color:'#a09e99', marginBottom:8 }}>{p.model}{p.due_date ? ' · 납기 '+p.due_date : ''}</div>
                <div style={{ fontSize:12, color:'#6b6860', lineHeight:1.5 }}>
                  {p.content?.length > 60 ? p.content.slice(0,60)+'…' : p.content}
                </div>
                {p.image_urls?.length > 0 && (
                  <div style={{ marginTop:8, fontSize:11, color:'#4a8c60' }}>🖼️ 도면 {p.image_urls.length}장</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 등록 모달 */}
      {showModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && resetModal()}>
          <div style={{ background:'#fff', borderRadius:14, width:520, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:15, fontWeight:700 }}>{editingId ? '제작 프로젝트 수정' : '제작 프로젝트 등록'}</span>
              <button type="button" onClick={resetModal} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:'20px 24px' }}>
              <div style={{ background:'#e8eef8', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12, color:'#1e4a8c' }}>
                예시: <b>26년 3월 롯데오카도</b>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>연도 (YY)</label>
                  <input value={form.year} onChange={e => setForm({...form, year:e.target.value})}
                    placeholder="예) 26" maxLength={2}
                    style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>월</label>
                  <select value={form.month} onChange={e => setForm({...form, month:e.target.value})}
                    style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit' }}>
                    {['1월','2월','3월','4월','5월','6월','7월','8월','9월','10월','11월','12월'].map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>고객사</label>
                  <input value={form.client} onChange={e => setForm({...form, client:e.target.value})}
                    placeholder="예) 롯데오카도"
                    style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>차종 / 모델</label>
                  <input value={form.model} onChange={e => setForm({...form, model:e.target.value})}
                    placeholder="예) 냉동탑차 5t"
                    style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>담당자</label>
                  <input value={form.worker} onChange={e => setForm({...form, worker:e.target.value})}
                    placeholder="담당자 이름"
                    style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>납기일</label>
                  <input type="date" value={form.due} onChange={e => setForm({...form, due:e.target.value})}
                    style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
                </div>
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>제작 내용 / 특이사항</label>
                <textarea value={form.content} onChange={e => setForm({...form, content:e.target.value})}
                  placeholder="제작 사양, 특이사항, 메모 등" rows={3}
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', resize:'vertical', boxSizing:'border-box' }} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>도면 이미지 첨부</label>
                <div onClick={() => document.getElementById('work-file-input').click()}
                  style={{ border:'1.5px dashed #e5e3de', borderRadius:8, padding:18, textAlign:'center', cursor:'pointer', background:'#f9f8f6' }}>
                  <div style={{ fontSize:22, marginBottom:5 }}>🖼️</div>
                  <div style={{ fontSize:12, color:'#a09e99' }}>PNG, JPG 클릭하여 첨부 (복수 선택 가능)</div>
                </div>
                <input id="work-file-input" type="file" accept=".png,.jpg,.jpeg" multiple onChange={handleFiles} style={{ display:'none' }} />
                {editingId && existingImageUrls.length > 0 && (
                  <div style={{ marginBottom:10 }}>
                    <div style={{ fontSize:11, color:'#6b6860', marginBottom:6 }}>기존 도면 (× 로 삭제)</div>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                      {existingImageUrls.map((src, i) => (
                        <div key={src + i} style={{ position:'relative' }}>
                          <img src={src} alt="" style={{ width:72, height:60, borderRadius:6, objectFit:'cover', border:'1px solid #e5e3de' }} />
                          <button type="button" onClick={() => setExistingImageUrls(u => u.filter((_, j) => j !== i))}
                            style={{ position:'absolute', top:-6, right:-6, width:22, height:22, borderRadius:'50%', border:'none', background:'#fdeaea', color:'#b83030', cursor:'pointer', fontSize:12, lineHeight:1 }}>×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {previews.length > 0 && (
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:10 }}>
                    {previews.map((src, i) => (
                      <div key={i} style={{ position:'relative' }}>
                        <img src={src} alt="" style={{ width:72, height:60, borderRadius:6, objectFit:'cover', border:'1px solid #e5e3de' }} />
                        <button type="button" onClick={() => removeNewImageAt(i)}
                          style={{ position:'absolute', top:-6, right:-6, width:22, height:22, borderRadius:'50%', border:'none', background:'#fdeaea', color:'#b83030', cursor:'pointer', fontSize:12, lineHeight:1 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button type="button" onClick={resetModal}
                style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>취소</button>
              <button type="button" onClick={submitProject} disabled={loading}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                {loading ? (editingId ? '저장 중...' : '등록 중...') : (editingId ? '저장하기' : '등록하기')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 상세 모달 */}
      {showDetail && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setShowDetail(null)}>
          <div style={{ background:'#fff', borderRadius:14, width:620, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:15, fontWeight:700 }}>{showDetail.year}년 {showDetail.month} · {showDetail.client}</span>
              <button onClick={() => setShowDetail(null)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:'20px 24px' }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:18 }}>
                {[['고객사', showDetail.client], ['차종/모델', showDetail.model], ['담당자', showDetail.worker],
                  ['연도/월', showDetail.year+'년 '+showDetail.month], ['납기일', showDetail.due_date||'—']].map(([k,v]) => (
                  <div key={k}>
                    <div style={{ fontSize:10, color:'#a09e99', marginBottom:3 }}>{k}</div>
                    <div style={{ fontSize:13, fontWeight: k==='고객사'?700:400 }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom:18 }}>
                <div style={{ fontSize:11, color:'#a09e99', marginBottom:6 }}>제작 내용 / 특이사항</div>
                <div style={{ background:'#f9f8f6', borderRadius:8, padding:12, fontSize:13, lineHeight:1.7, border:'1px solid #e5e3de' }}>{showDetail.content}</div>
              </div>
              <div>
                <div style={{ fontSize:11, color:'#a09e99', marginBottom:8 }}>도면 이미지 ({showDetail.image_urls?.length || 0}장)</div>
                {showDetail.image_urls?.length > 0 ? (
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    {showDetail.image_urls.map((src, i) => (
                      <img key={i} src={src} alt={`도면${i+1}`}
                        onClick={() => window.open(src, '_blank')}
                        style={{ width:100, height:80, borderRadius:6, objectFit:'cover', border:'1px solid #e5e3de', cursor:'pointer' }} />
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize:13, color:'#a09e99', background:'#f9f8f6', borderRadius:8, padding:16, textAlign:'center', border:'1px solid #e5e3de' }}>
                    첨부된 도면 이미지가 없습니다
                  </div>
                )}
              </div>
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
              <button type="button" onClick={() => deleteProject(showDetail.id)}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#fdeaea', color:'#b83030', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                🗑️ 삭제
              </button>
              <div style={{ display:'flex', gap:10 }}>
                <button type="button" onClick={() => openEditModal(showDetail)}
                  style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#e8f2eb', color:'#2d5a3d', cursor:'pointer', fontSize:13, fontWeight:600 }}>
                  ✏️ 수정
                </button>
                <button type="button" onClick={() => setShowDetail(null)}
                  style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>닫기</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
